import { useState, useCallback } from "react";
import type { IntroductionLetterTemplate } from "../types/introduction-letter";
import apiService from "../../../../service/Api";

export const useIntroductionLetterTemplates = () => {
  const [templates, setTemplates] = useState<IntroductionLetterTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async (category?: string) => {
    setLoading(true);
    setError(null);

    try {
      const url = category
        ? `/api/configurations/introduction-letter-templates/?category=${category}`
        : "/api/configurations/introduction-letter-templates/";

      const response = await apiService.get<
        IntroductionLetterTemplate[] | { results: IntroductionLetterTemplate[] }
      >(url);
      const data = response.data;
      setTemplates(Array.isArray(data) ? data : data.results);
    } catch (err: unknown) {
      console.error("[v0] Template fetch error:", err);
      setError((err as { message?: string })?.message || "Failed to fetch templates");
    } finally {
      setLoading(false);
    }
  }, []);

  const saveTemplate = useCallback(
    async (
      template: Omit<IntroductionLetterTemplate, "id" | "createdAt">
    ): Promise<IntroductionLetterTemplate> => {
      try {
        const { data } = await apiService.post<IntroductionLetterTemplate>(
          "/api/configurations/introduction-letter-templates/",
          template
        );
        setTemplates((prev) => [...prev, data]);
        return data;
      } catch (err: unknown) {
        setError((err as { message?: string })?.message || "Failed to save template");
        throw err;
      }
    },
    []
  );

  const updateTemplate = useCallback(
    async (id: string, updates: Partial<IntroductionLetterTemplate>) => {
      try {
        const { data } = await apiService.patch<IntroductionLetterTemplate>(
          `/api/configurations/introduction-letter-templates/${id}/`,
          updates
        );
        setTemplates((prev) => prev.map((t) => (t.id === id ? data : t)));
      } catch (err: unknown) {
        setError((err as { message?: string })?.message || "Failed to update template");
        throw err;
      }
    },
    []
  );

  const deleteTemplate = useCallback(async (id: string) => {
    try {
      await apiService.delete(
        `/api/configurations/introduction-letter-templates/${id}/`
      );
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || "Failed to delete template");
      throw err;
    }
  }, []);

  return {
    templates,
    loading,
    error,
    fetchTemplates,
    saveTemplate,
    updateTemplate,
    deleteTemplate,
  };
};
